// ─── Shared Facebook Lead Ads sync helpers ────────────────────────────────────
// Used by BOTH the Integrations page (manual "Sync" button, first-time field
// mapping) and the background auto-sync effect in CRMContext.js (silent
// re-sync every 15 minutes for as long as a CRM tab is open — no backend/cron
// involved, per explicit instruction not to touch backend infrastructure).
// Keeping the Graph API call + lead-mapping logic in one place means the two
// call sites can never drift apart.

export async function graphAPI(path, params = {}) {
  const url = new URL(`https://graph.facebook.com/v19.0${path}`);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
  });
  const res = await fetch(url.toString());
  return res.json();
}

// Auto-guess CRM field from FB field name — used only the FIRST time a form
// is synced, to pre-fill the mapping dialog. After the admin confirms (or
// edits) the mapping once, it's persisted and this guess is never consulted
// again for that form.
export function guessMapping(fbKey) {
  const k = fbKey.toLowerCase();
  if (k.includes('full_name') || k === 'name')                   return 'firstName';
  if (k.includes('first_name') || k === 'first')                 return 'firstName';
  if (k.includes('last_name')  || k === 'last')                  return 'lastName';
  if (k.includes('phone') || k.includes('mobile') || k.includes('whatsapp')) return 'phone';
  if (k.includes('email') || k.includes('mail'))                 return 'email';
  if (k.includes('city'))                                        return 'city';
  if (k.includes('country') || k.includes('location'))          return 'country';
  if (k.includes('course') || k.includes('program') || k.includes('programme') || k.includes('service')) return 'course';
  if (k.includes('budget') || k.includes('amount'))             return 'budget';
  return '__skip__';
}

// Map a raw FB lead → CRM lead shape using an explicit field mapping.
export function mapFBLead(fbLead, pageId, pageName, formId, formName, fieldMapping, adData) {
  const mapped = {
    source: 'facebook',
    priority: 'warm',
    fbLeadId:   fbLead.id,
    fbFormId:   formId,
    fbFormName: formName,
    fbPageId:   pageId,
    fbPageName: pageName || '',
    fbAdId:         adData?.ad_id         || fbLead.ad_id         || '',
    fbAdName:       adData?.ad_name       || fbLead.ad_name       || '',
    fbAdsetId:      adData?.adset_id      || fbLead.adset_id      || '',
    fbAdsetName:    adData?.adset_name    || fbLead.adset_name    || '',
    fbCampaignId:   adData?.campaign_id   || fbLead.campaign_id   || '',
    fbCampaignName: adData?.campaign_name || fbLead.campaign_name || '',
    fbPlatform:     fbLead.platform       || '',
    fbCreatedTime:  fbLead.created_time   || '',
  };

  (fbLead.field_data || []).forEach(f => {
    const crmKey = fieldMapping[f.name];
    if (!crmKey || crmKey === '__skip__') return;
    const val = (f.values || [])[0] || '';
    if (!val) return;
    if (crmKey === 'firstName' && val.includes(' ') && !mapped.lastName) {
      const parts = val.split(' ');
      mapped.firstName = parts[0];
      mapped.lastName  = parts.slice(1).join(' ');
    } else {
      mapped[crmKey] = val;
    }
  });

  if (!mapped.firstName && !mapped.name) mapped.firstName = `FB Lead ${fbLead.id.slice(-6)}`;
  if (!mapped.phone) mapped.phone = '';
  return mapped;
}

// Fetch a form's leads from the Graph API and split into already-imported vs new.
export async function fetchNewFormLeads(formId, pageAccessToken, existingFbIds) {
  const data = await graphAPI(`/${formId}/leads`, {
    access_token: pageAccessToken,
    fields: 'field_data,created_time,id,form_id,ad_id,ad_name,adset_id,adset_name,campaign_id,campaign_name,platform',
    limit: 200,
  });
  if (data.error) return { error: data.error };
  const all = data.data || [];
  const newLeads = all.filter(l => !existingFbIds.has(l.id));
  return { all, newLeads };
}

// ─── Facebook JS SDK login helpers (shared) ───────────────────────────────────
// Same scope/flow Integrations.js already uses for the Meta Ads connect step —
// duplicated here as standalone exports (rather than imported FROM
// Integrations.js, which has no exports) so DigitalMedia.js's live ad-spend
// view can open its own short-lived FB session without touching the
// already-verified Integrations.js OAuth code at all.
export const FB_SCOPE = 'leads_retrieval,pages_read_engagement,pages_show_list,pages_manage_metadata,pages_manage_ads,ads_read,business_management';

export function loadFBSDK(appId) {
  return new Promise((resolve) => {
    const init = () => {
      window.FB.init({ appId, version: 'v19.0', xfbml: false, cookie: true, status: true });
      resolve(window.FB);
    };
    if (window.FB) { init(); return; }
    window.fbAsyncInit = init;
    if (!document.getElementById('fb-jssdk')) {
      const s = document.createElement('script');
      s.id = 'fb-jssdk';
      s.src = 'https://connect.facebook.net/en_US/sdk.js';
      s.async = true;
      document.head.appendChild(s);
    }
  });
}

// Returns a fresh access token silently if already authorised, or null if not.
export function fbGetLoginStatus(FB) {
  return new Promise((resolve) => {
    FB.getLoginStatus((r) => {
      resolve(r.status === 'connected' ? r.authResponse.accessToken : null);
    }, true); // true = force a fresh check with Facebook's servers
  });
}

// Triggers the FB login dialog and returns the token PLUS which scopes were
// actually granted. A user can untick individual permissions (like ads_read)
// in the consent dialog even though we requested the full FB_SCOPE — if we
// only returned the token, a silently-declined ads_read would look identical
// to a successful connection right up until spend queries come back empty
// with no explanation. Callers should check grantedScopes before assuming
// ad-spend reads will actually work.
export function fbLogin(FB) {
  return new Promise((resolve, reject) => {
    FB.login((r) => {
      if (r.authResponse?.accessToken) {
        resolve({ accessToken: r.authResponse.accessToken, grantedScopes: (r.authResponse.grantedScopes || '').split(',').filter(Boolean) });
      } else {
        reject(new Error(r.status === 'not_authorized' ? 'App not authorized. Please approve all permissions.' : 'Login window was closed.'));
      }
    }, { scope: FB_SCOPE, return_scopes: true, auth_type: 'rerequest' });
  });
}

// ─── Ad-spend helpers (new — powers the live spend view on DigitalMedia.js) ──
// These are ad-ACCOUNT-level Marketing API calls (/act_{id}/...), a different
// part of the Graph API from the page-level lead-form calls above. Nothing
// here writes anything — read-only spend reporting.

// List the ad accounts the logged-in FB user can see, so an admin/marketer
// can pick which one this CRM should track. Returns [] (not an error) if the
// token lacks ads_read or the user has no ad accounts, so callers can show a
// friendly empty state instead of a scary error.
export async function fetchAdAccounts(token) {
  const data = await graphAPI('/me/adaccounts', {
    access_token: token,
    fields: 'id,account_id,name,currency,account_status',
    limit: 200,
  });
  if (data.error) return { error: data.error, accounts: [] };
  return { accounts: data.data || [] };
}

// Fetch every ACTIVE campaign in an ad account, each with today's and this
// month's spend so far, PLUS the ad account's own top-line totals for the
// same two windows. Facebook's own reporting has a short lag (typically
// well under an hour) — "real time" here means "as fresh as Facebook will
// give us," refreshed by polling rather than any push mechanism (consistent
// with the rest of this integration's no-backend, client-poll design).
//
// Two different kinds of "no number" can happen here and callers should NOT
// treat them the same:
//  - Facebook legitimately reports zero spend (real ₹0 — campaign isn't
//    delivering right now).
//  - Facebook didn't return an insights object for that campaign at all,
//    most commonly because the token has campaign-list visibility but not
//    insights/financial read access on this ad account (e.g. ads_read was
//    declined at login, or the FB user only has a limited Business Manager
//    role). This should show as "no data," not as "₹0 spent," or it looks
//    like the campaign is dormant when actually we just can't see its spend.
// We mark the difference with insightsAvailable, and ALSO fetch the account-
// level total independently (a single, simpler call) so the top-line
// "Spent Today / This Month" tiles aren't silently wrong just because the
// nested per-campaign field expansion happened to come back empty.
// extraCampaignIds: FB campaign IDs the CRM already knows about (because real
// leads are tagged with them) that we want spend for EVEN IF Facebook no
// longer reports them as ACTIVE. A campaign that generated real leads but has
// since been paused, completed, or hit its budget is exactly the one a
// marketer most wants total cost-per-lead for — restricting the whole feature
// to effective_status=ACTIVE would silently hide it forever once it stops
// running, which is the opposite of useful here.
export async function fetchActiveCampaignSpend(token, adAccountId, extraCampaignIds = []) {
  const acctPath = adAccountId.startsWith('act_') ? adAccountId : `act_${adAccountId}`;
  const [todayRes, monthRes, acctTodayRes, acctMonthRes] = await Promise.all([
    graphAPI(`/${acctPath}/campaigns`, {
      access_token: token,
      effective_status: JSON.stringify(['ACTIVE']),
      fields: 'id,name,objective,daily_budget,lifetime_budget,insights.date_preset(today){spend,impressions,clicks}',
      limit: 200,
    }),
    graphAPI(`/${acctPath}/campaigns`, {
      access_token: token,
      effective_status: JSON.stringify(['ACTIVE']),
      fields: 'id,name,insights.date_preset(this_month){spend,impressions,clicks}',
      limit: 200,
    }),
    // Account-level totals via the plain /insights edge — a much simpler
    // call than the nested per-campaign field expansion above, so it's a
    // useful cross-check when the per-campaign numbers look suspiciously
    // all-zero.
    graphAPI(`/${acctPath}/insights`, { access_token: token, date_preset: 'today', fields: 'spend,impressions,clicks' }),
    graphAPI(`/${acctPath}/insights`, { access_token: token, date_preset: 'this_month', fields: 'spend,impressions,clicks' }),
  ]);
  if (todayRes.error) return { error: todayRes.error };
  if (monthRes.error) return { error: monthRes.error };
  // Account-level insights failing isn't fatal to the per-campaign table —
  // just means we can't show the top-line cross-check totals.
  const acctError = acctTodayRes.error || acctMonthRes.error || null;

  const monthById = {};
  (monthRes.data || []).forEach(c => { monthById[c.id] = c.insights?.data?.[0]; });

  let anyInsightsAvailable = false;
  const campaigns = (todayRes.data || []).map(c => {
    const todayInsight = c.insights?.data?.[0];
    const monthInsight = monthById[c.id];
    const hasToday = !!todayInsight;
    const hasMonth = !!monthInsight;
    if (hasToday || hasMonth) anyInsightsAvailable = true;
    return {
      id: c.id,
      name: c.name,
      objective: c.objective || '',
      dailyBudget: c.daily_budget ? Number(c.daily_budget) / 100 : null,
      lifetimeBudget: c.lifetime_budget ? Number(c.lifetime_budget) / 100 : null,
      insightsAvailable: hasToday || hasMonth,
      spendToday: hasToday ? Number(todayInsight.spend || 0) : null,
      spendMonth: hasMonth ? Number(monthInsight.spend || 0) : null,
      impressionsToday: hasToday ? Number(todayInsight.impressions || 0) : null,
      clicksToday: hasToday ? Number(todayInsight.clicks || 0) : null,
    };
  });

  const acctSpendToday = acctTodayRes.data?.[0]?.spend != null ? Number(acctTodayRes.data[0].spend) : null;
  const acctSpendMonth = acctMonthRes.data?.[0]?.spend != null ? Number(acctMonthRes.data[0].spend) : null;

  // Fetch any CRM-known campaign IDs that didn't show up in the active list
  // above, one campaign at a time (there are normally only a handful of
  // distinct campaigns feeding any one CRM, so this stays cheap). Each is
  // marked notCurrentlyActive so the UI can explain why it's listed even
  // though it's not in Facebook's active set.
  const seenIds = new Set(campaigns.map(c => c.id));
  const missingIds = (extraCampaignIds || []).filter(id => id && !seenIds.has(id));
  if (missingIds.length > 0) {
    const extraResults = await Promise.all(missingIds.map(id => Promise.all([
      graphAPI(`/${id}`, { access_token: token, fields: 'id,name,objective,daily_budget,lifetime_budget,effective_status,insights.date_preset(today){spend,impressions,clicks}' }),
      graphAPI(`/${id}`, { access_token: token, fields: 'insights.date_preset(this_month){spend,impressions,clicks}' }),
    ])));
    extraResults.forEach(([c, m]) => {
      if (c.error) return; // token can't see this specific campaign — skip rather than fail the whole page
      const todayInsight = c.insights?.data?.[0];
      const monthInsight = m.insights?.data?.[0];
      const hasToday = !!todayInsight;
      const hasMonth = !!monthInsight;
      if (hasToday || hasMonth) anyInsightsAvailable = true;
      campaigns.push({
        id: c.id,
        name: c.name,
        objective: c.objective || '',
        dailyBudget: c.daily_budget ? Number(c.daily_budget) / 100 : null,
        lifetimeBudget: c.lifetime_budget ? Number(c.lifetime_budget) / 100 : null,
        insightsAvailable: hasToday || hasMonth,
        spendToday: hasToday ? Number(todayInsight.spend || 0) : null,
        spendMonth: hasMonth ? Number(monthInsight.spend || 0) : null,
        impressionsToday: hasToday ? Number(todayInsight.impressions || 0) : null,
        clicksToday: hasToday ? Number(todayInsight.clicks || 0) : null,
        notCurrentlyActive: true,
        effectiveStatus: c.effective_status || '',
      });
    });
  }

  return {
    campaigns,
    accountSpendToday: acctSpendToday,
    accountSpendMonth: acctSpendMonth,
    // True when Facebook gave us NEITHER per-campaign NOR account-level
    // numbers — the clearest sign this token can list campaigns but can't
    // actually read their financials (a permissions problem, not a "spend
    // is genuinely zero" situation).
    noInsightsAccess: !anyInsightsAvailable && acctSpendToday == null && acctSpendMonth == null,
    acctInsightsError: acctError,
  };
}
